import { z } from "zod";
import { registerSchema, loginSchema, insertBusinessSchema, insertReviewSchema, insertCommentSchema, insertAdSchema, insertCategorySchema } from "./schema";

// === SHARED ERROR SCHEMAS ===
export const errorSchemas = {
  validation: z.object({ message: z.string(), field: z.string().optional() }),
  notFound: z.object({ message: z.string() }),
  unauthorized: z.object({ message: z.string() }),
  internal: z.object({ message: z.string() }),
};

// === API CONTRACT ===
export const api = {
  auth: {
    register: {
      method: "POST" as const,
      path: "/api/auth/register" as const,
      input: registerSchema,
      responses: {
        201: z.object({ id: z.number(), username: z.string(), email: z.string(), role: z.string() }),
        400: errorSchemas.validation,
      },
    },
    login: {
      method: "POST" as const,
      path: "/api/auth/login" as const,
      input: loginSchema,
      responses: {
        200: z.object({ id: z.number(), username: z.string(), email: z.string(), role: z.string() }),
        401: errorSchemas.unauthorized,
      },
    },
    logout: {
      method: "POST" as const,
      path: "/api/auth/logout" as const,
      responses: { 200: z.object({ message: z.string() }) },
    },
    logoutAll: {
      method: "POST" as const,
      path: "/api/auth/logout-all" as const,
      responses: { 200: z.object({ message: z.string() }) },
    },
    me: {
      method: "GET" as const,
      path: "/api/auth/me" as const,
      responses: {
        200: z.object({
          id: z.number(),
          username: z.string(),
          email: z.string(),
          role: z.string(),
          avatarUrl: z.string().nullable(),
          forcePasswordReset: z.boolean().optional(),
        }).nullable(),
      },
    },
    forgotPassword: {
      method: "POST" as const,
      path: "/api/auth/forgot-password" as const,
      input: z.object({ email: z.string().email() }),
      responses: {
        200: z.object({ message: z.string() }),
        400: errorSchemas.validation,
      },
    },
    resetPassword: {
      method: "POST" as const,
      path: "/api/auth/reset-password" as const,
      input: z.object({ token: z.string(), newPassword: z.string().min(8) }),
      responses: {
        200: z.object({ message: z.string() }),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
      },
    },
    validateResetToken: {
      method: "GET" as const,
      path: "/api/auth/validate-reset-token" as const,
      input: z.object({ token: z.string() }),
      responses: {
        200: z.object({ valid: z.boolean() }),
        400: errorSchemas.validation,
      },
    },
  },

  businesses: {
    list: {
      method: "GET" as const,
      path: "/api/businesses" as const,
      input: z.object({
        search: z.string().optional(),
        categoryId: z.string().optional(),
        isOnline: z.string().optional(),
        isInPerson: z.string().optional(),
        city: z.string().optional(),
        lat: z.string().optional(),
        lng: z.string().optional(),
        radiusKm: z.string().optional(),
        isFeatured: z.string().optional(),
        status: z.enum(["pending", "approved", "rejected", "all", "archived"]).optional(),
        page: z.string().optional(),
        limit: z.string().optional(),
      }).optional(),
      responses: {
        200: z.object({
          items: z.array(z.any()),
          total: z.number(),
          page: z.number(),
          limit: z.number(),
          totalPages: z.number(),
        }),
      },
    },
    get: {
      method: "GET" as const,
      path: "/api/businesses/:id" as const,
      responses: {
        200: z.any(),
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/businesses" as const,
      input: insertBusinessSchema,
      responses: {
        201: z.any(),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/businesses/:id" as const,
      input: insertBusinessSchema.partial(),
      responses: {
        200: z.any(),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/businesses/:id" as const,
      responses: {
        204: z.void(),
        401: errorSchemas.unauthorized,
        404: errorSchemas.notFound,
      },
    },
    // Accepts all three statuses so admins can move a listing back to pending
    updateStatus: {
      method: "PATCH" as const,
      path: "/api/businesses/:id/status" as const,
      input: z.object({ status: z.enum(["approved", "rejected", "pending"]) }),
      responses: {
        200: z.any(),
        401: errorSchemas.unauthorized,
        404: errorSchemas.notFound,
      },
    },
    toggleFeatured: {
      method: "PATCH" as const,
      path: "/api/businesses/:id/featured" as const,
      input: z.object({ isFeatured: z.boolean() }),
      responses: {
        200: z.any(),
        401: errorSchemas.unauthorized,
        404: errorSchemas.notFound,
      },
    },
    archive: {
      method: "PATCH" as const,
      path: "/api/businesses/:id/archive" as const,
      input: z.object({ isArchived: z.boolean() }),
      responses: {
        200: z.any(),
        401: errorSchemas.unauthorized,
        404: errorSchemas.notFound,
      },
    },
  },

  distanceMatrix: {
    method: "POST" as const,
    path: "/api/distance-matrix" as const,
    input: z.object({
      origin: z.object({ lat: z.number(), lng: z.number() }),
      destinations: z.array(z.object({ id: z.number(), lat: z.number(), lng: z.number() })).min(1).max(25),
      mode: z.enum(["driving", "walking", "bicycling", "transit"]).optional(),
    }),
    responses: {
      200: z.array(z.object({
        id: z.number(),
        distanceText: z.string().nullable(),
        distanceMeters: z.number().nullable(),
        durationText: z.string().nullable(),
        durationSeconds: z.number().nullable(),
      })),
    },
  },

  categories: {
    list: {
      method: "GET" as const,
      path: "/api/categories" as const,
      responses: { 200: z.array(z.any()) },
    },
    create: {
      method: "POST" as const,
      path: "/api/categories" as const,
      input: insertCategorySchema,
      responses: {
        201: z.any(),
        401: errorSchemas.unauthorized,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/categories/:id" as const,
      responses: {
        204: z.void(),
        401: errorSchemas.unauthorized,
      },
    },
  },

  reviews: {
    list: {
      method: "GET" as const,
      path: "/api/businesses/:businessId/reviews" as const,
      responses: { 200: z.array(z.any()) },
    },
    create: {
      method: "POST" as const,
      path: "/api/businesses/:businessId/reviews" as const,
      input: z.object({ rating: z.number().min(1).max(5), comment: z.string().optional() }),
      responses: {
        201: z.any(),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/reviews/:id" as const,
      responses: {
        204: z.void(),
        401: errorSchemas.unauthorized,
        404: errorSchemas.notFound,
      },
    },
  },

  comments: {
    list: {
      method: "GET" as const,
      path: "/api/businesses/:businessId/comments" as const,
      responses: { 200: z.array(z.any()) },
    },
    create: {
      method: "POST" as const,
      path: "/api/businesses/:businessId/comments" as const,
      input: z.object({ content: z.string().min(1), parentId: z.number().optional() }),
      responses: {
        201: z.any(),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/comments/:id" as const,
      responses: {
        204: z.void(),
        401: errorSchemas.unauthorized,
        404: errorSchemas.notFound,
      },
    },
  },

  ads: {
    list: {
      method: "GET" as const,
      path: "/api/ads" as const,
      responses: { 200: z.array(z.any()) },
    },
    create: {
      method: "POST" as const,
      path: "/api/ads" as const,
      input: insertAdSchema,
      responses: {
        201: z.any(),
        401: errorSchemas.unauthorized,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/ads/:id" as const,
      input: insertAdSchema.partial(),
      responses: {
        200: z.any(),
        401: errorSchemas.unauthorized,
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/ads/:id" as const,
      responses: {
        204: z.void(),
        401: errorSchemas.unauthorized,
      },
    },
  },

  notifications: {
    list: {
      method: "GET" as const,
      path: "/api/notifications" as const,
      responses: { 200: z.array(z.any()) },
    },
    markRead: {
      method: "PATCH" as const,
      path: "/api/notifications/:id/read" as const,
      responses: { 200: z.any() },
    },
    markAllRead: {
      method: "PATCH" as const,
      path: "/api/notifications/read-all" as const,
      responses: { 200: z.object({ message: z.string() }) },
    },
  },

  publicStats: {
    method: "GET" as const,
    path: "/api/public-stats" as const,
    responses: {
      200: z.object({
        totalBusinesses: z.number(),
        totalUsers: z.number(),
        totalReviews: z.number(),
      }),
    },
  },

  account: {
    updateEmail: {
      method: "PATCH" as const,
      path: "/api/account/email" as const,
      input: z.object({ email: z.string().email() }),
      responses: { 200: z.object({ email: z.string() }), 400: errorSchemas.validation, 401: errorSchemas.unauthorized },
    },
    updatePassword: {
      method: "PATCH" as const,
      path: "/api/account/password" as const,
      input: z.object({ currentPassword: z.string().min(1), newPassword: z.string().min(8) }),
      responses: { 200: z.object({ message: z.string() }), 400: errorSchemas.validation, 401: errorSchemas.unauthorized },
    },
    loginHistory: {
      method: "GET" as const,
      path: "/api/account/login-history" as const,
      responses: { 200: z.array(z.any()), 401: errorSchemas.unauthorized },
    },
    apiKeys: {
      list: {
        method: "GET" as const,
        path: "/api/account/api-keys" as const,
        responses: { 200: z.array(z.any()), 401: errorSchemas.unauthorized },
      },
      create: {
        method: "POST" as const,
        path: "/api/account/api-keys" as const,
        input: z.object({ name: z.string().min(2).max(40) }),
        responses: { 201: z.any(), 400: errorSchemas.validation, 401: errorSchemas.unauthorized },
      },
      revoke: {
        method: "DELETE" as const,
        path: "/api/account/api-keys/:id" as const,
        responses: { 200: z.any(), 401: errorSchemas.unauthorized },
      },
    },
  },

  admin: {
    stats: {
      method: "GET" as const,
      path: "/api/admin/stats" as const,
      responses: { 200: z.any(), 401: errorSchemas.unauthorized },
    },
    users: {
      method: "GET" as const,
      path: "/api/admin/users" as const,
      responses: { 200: z.array(z.any()), 401: errorSchemas.unauthorized },
    },
    updateUserRole: {
      method: "PATCH" as const,
      path: "/api/admin/users/:id/role" as const,
      input: z.object({ role: z.enum(["user", "business_owner", "admin", "drabpe_team", "drabpe_board"]) }),
      responses: { 200: z.any(), 401: errorSchemas.unauthorized },
    },
    updateUserProfile: {
      method: "PATCH" as const,
      path: "/api/admin/users/:id" as const,
      input: z.object({
        username: z.string().min(2).max(64).optional(),
        email: z.string().email().optional(),
      }),
      responses: { 200: z.any(), 401: errorSchemas.unauthorized },
    },
    resetUserPassword: {
      method: "POST" as const,
      path: "/api/admin/users/:id/reset-password" as const,
      responses: { 200: z.object({ tempPassword: z.string() }), 401: errorSchemas.unauthorized },
    },
    reviews: {
      method: "GET" as const,
      path: "/api/admin/reviews" as const,
      responses: { 200: z.array(z.any()), 401: errorSchemas.unauthorized },
    },
    comments: {
      method: "GET" as const,
      path: "/api/admin/comments" as const,
      responses: { 200: z.array(z.any()), 401: errorSchemas.unauthorized },
    },
    loginBlocks: {
      method: "GET" as const,
      path: "/api/admin/login-blocks" as const,
      responses: { 200: z.array(z.any()), 401: errorSchemas.unauthorized },
    },
    emailDnsSettings: {
      method: "GET" as const,
      path: "/api/admin/email-dns" as const,
      responses: { 200: z.any(), 401: errorSchemas.unauthorized },
    },
    updateEmailDnsSettings: {
      method: "PUT" as const,
      path: "/api/admin/email-dns" as const,
      input: z.object({
        forwardingFrom: z.string().optional(),
        forwardingTo: z.string().optional(),
        hostingerMx: z.array(z.string()).optional(),
        hostingerSpf: z.string().optional(),
        hostingerDkim: z.string().optional(),
        hostingerDmarc: z.string().optional(),
        workspaceMx: z.array(z.string()).optional(),
        workspaceRouting: z.string().optional(),
      }),
      responses: { 200: z.any(), 401: errorSchemas.unauthorized },
    },
    deleteReview: {
      method: "DELETE" as const,
      path: "/api/admin/reviews/:id" as const,
      responses: { 204: z.void(), 401: errorSchemas.unauthorized },
    },
    deleteComment: {
      method: "DELETE" as const,
      path: "/api/admin/comments/:id" as const,
      responses: { 204: z.void(), 401: errorSchemas.unauthorized },
    },
  },
};

// === BUILD URL HELPER ===
export function buildUrl(path: string, params?: Record<string, string | number>): string {
  let url = path;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (url.includes(`:${key}`)) {
        url = url.replace(`:${key}`, String(value));
      }
    });
  }
  return url;
}

// === TYPE HELPERS ===
export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
